import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { updateMarginMongo } from '../../util/api';

// ================ Async Thunks ================ //

/**
 * Update the current user's profile and MongoDB with a new margin percentage.
 */
export const updateMarginPercentageThunk = createAsyncThunk(
  'AdminMarginPage/updateMarginPercentage',
  (marginPercentage, { dispatch, rejectWithValue, extra: sdk, getState }) => {
    const margin = parseFloat(marginPercentage);
    const existingPublicData = getState().user.currentUser?.attributes?.profile?.publicData || {};
    const actionPayload = {
      publicData: {
        ...existingPublicData,
        marginPercentage: margin,
      },
    };

    // Update both Sharetribe profile and MongoDB
    return Promise.all([
      sdk.currentUser.updateProfile(actionPayload),
      updateMarginMongo({ marginPercentage: margin }),
    ])
      .then(([sdkResponse]) => {
        // Ensure full currentUser entity (including all publicData keys like isAdmin)
        // is refreshed immediately after save.
        return dispatch(
          fetchCurrentUser({
            enforce: true,
            updateHasListings: false,
            updateNotifications: false,
          })
        ).then(() => sdkResponse);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const updateMarginPercentage = actionPayload => dispatch => {
  return dispatch(updateMarginPercentageThunk(actionPayload));
};

// ================ Slice ================ //

const adminMarginPageSlice = createSlice({
  name: 'AdminMarginPage',
  initialState: {
    updateInProgress: false,
    updateError: null,
    updateSuccess: false,
  },
  reducers: {
    clearUpdateError: state => {
      state.updateError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(updateMarginPercentageThunk.pending, state => {
        state.updateInProgress = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateMarginPercentageThunk.fulfilled, state => {
        state.updateInProgress = false;
        state.updateSuccess = true;
      })
      .addCase(updateMarginPercentageThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      });
  },
});

export const { clearUpdateError } = adminMarginPageSlice.actions;
export default adminMarginPageSlice.reducer;

/**
 * Load initial data for the page.
 */
export const loadData = () => (dispatch, getState, sdk) => {
  // Current user is already loaded in the top-level app component.
  return Promise.resolve();
};
